/**
 * apps/web/src/components/EstimatesPanel.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Render structured estimates as a simple card list with status and subtotal.
 *
 * High-level data flow:
 *   - AuditViewerPage passes `detail.estimates` from the route loader.
 *   - This component renders one card per estimate in source order.
 */

import React from 'react';
import { Badge, Card, SimpleGrid, Stack, Text, Title } from '@mantine/core';

import type { AuditEstimate } from '@tinypro-audit/contracts';

/** Currency formatter used for estimate subtotals. */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/** Choose a stable badge color for a ServiceTitan estimate status. */
function estimateStatusColor(status: string): string {
  const normalized = status.trim().toLowerCase();

  if (normalized === 'sold') return 'green';
  if (normalized === 'open') return 'blue';
  if (normalized === 'declined' || normalized === 'canceled' || normalized === 'cancelled') {
    return 'red';
  }

  return 'gray';
}

/** Return the card title, falling back to the estimate id when the name is blank. */
function estimateTitle(estimate: AuditEstimate): string {
  return estimate.name?.trim() ? estimate.name : `Estimate #${estimate.id}`;
}

/** Render estimate data as one card per estimate with a status badge and subtotal. */
export function EstimatesPanel({ estimates }: { estimates: AuditEstimate[] }): React.ReactElement {
  return (
    <Card withBorder radius="md">
      <Title order={4} mb="sm">
        Estimates
      </Title>

      {estimates.length === 0 ? (
        <Text c="dimmed" size="sm">
          No estimates available.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {estimates.map((estimate) => (
            <Card key={estimate.id} withBorder radius="md" p="md">
              <Stack gap="xs">
                <Badge color={estimateStatusColor(estimate.status)} variant="light">
                  {estimate.status}
                </Badge>

                <Text fw={700} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {estimateTitle(estimate)}
                </Text>

                <div>
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Subtotal
                  </Text>
                  <Text fw={600}>{currencyFormatter.format(estimate.subtotal)}</Text>
                </div>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Card>
  );
}
